import { Formik, FormikProps } from 'formik';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { forwardRef, useRef } from 'react';

import * as CustomerDetailOverviewActions from '../overview/store/customer-detail-overview-actions';
import { CustomerDetailOverviewAddress } from '../overview/components/customer-detail-overview-address';
import { CustomerDetailOverviewFormValues } from '../overview/models/customer-detail-overview-form-values';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import {
    customerDetailOverviewBaselineSelector,
    customerDetailOverviewFormDataSelector,
} from '../overview/store/customer-detail-overview-selectors';
import { customerDetailOverviewValidationSchema } from '../overview/customer-detail-overview-validation-schema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

/**
 * The member's addresses, on a tab of their own: Kitzbühel has 53, and on the
 * details they made the page long (#2261). They are part of the same record as
 * the details — this tab works on the form the details park in the store, so
 * one save keeps both.
 */
export const CustomerDetailAddresses = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const dispatch = useThunkDispatch();
    const formikRef = useRef<FormikProps<CustomerDetailOverviewFormValues>>(null);
    const { formValues, errors, touched } = useAppSelector(customerDetailOverviewFormDataSelector);
    const baseline = useAppSelector(customerDetailOverviewBaselineSelector);

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(CustomerDetailOverviewActions.updateCustomerDetail(values, isDirty, isValid, errs));
    });

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            initialValues={formValues}
            validationSchema={customerDetailOverviewValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formikRef}
        >
            {() => (
                <>
                    {/* Only the addresses: the rest of the record has its mark on
                        the details, where it is entered. */}
                    <PaloDirtyComparison<CustomerDetailOverviewFormValues>
                        baseline={baseline?.addresses}
                        normalize={values => values.addresses}
                    />
                    <CustomerDetailOverviewAddress />
                </>
            )}
        </Formik>
    );
});
